import { paginate } from './cursor.js';
import { devCompletionCategories, devCompletionItems, devCompletionSchema, devCompletionSections, devFranchises, devGames, devRoadmap, devRoadmapPhases, devRoadmapStepTrophies, devRoadmapSteps, devTrophies, devTrophyGroups, devTrophySets, } from './dev-fixtures.js';
export function devFixtureData() {
    return {
        franchises: devFranchises,
        games: devGames,
        trophySets: devTrophySets,
        trophyGroups: devTrophyGroups,
        trophies: devTrophies,
        roadmaps: [devRoadmap],
        roadmapPhases: devRoadmapPhases,
        roadmapSteps: devRoadmapSteps,
        roadmapStepTrophies: devRoadmapStepTrophies,
        roadmapStepMissables: [],
        roadmapStepMapLinks: [],
        completionSchemas: [devCompletionSchema],
        completionSections: devCompletionSections,
        completionCategories: devCompletionCategories,
        completionItems: devCompletionItems,
        customDisplayAssets: [],
        vmapsLinks: [],
    };
}
export function createMemoryApi(data = devFixtureData()) {
    const runs = new Map();
    const stepStates = new Map();
    const itemProgress = new Map();
    const notes = new Map();
    const matches = (game, request) => {
        const q = request.query.trim().toLowerCase();
        if (q && !game.title.toLowerCase().includes(q))
            return false;
        if (request.platform && !game.platforms.includes(request.platform))
            return false;
        if (request.supportState && game.supportState !== request.supportState)
            return false;
        return true;
    };
    const categoriesOfSchema = (schemaId) => {
        const sectionIds = new Set(data.completionSections.filter((s) => s.schemaId === schemaId).map((s) => s.id));
        return data.completionCategories.filter((c) => sectionIds.has(c.sectionId));
    };
    return {
        games: {
            searchGames(request) {
                const filtered = data.games.filter((g) => matches(g, request));
                return Promise.resolve(paginate(filtered, request.cursor, request.limit));
            },
            searchGamesGroupedByFranchise(request) {
                const filtered = data.games.filter((g) => matches(g, request));
                const byFranchise = new Map();
                for (const game of filtered) {
                    const key = game.franchiseId ?? null;
                    byFranchise.set(key, [...(byFranchise.get(key) ?? []), game]);
                }
                const groups = [...byFranchise.entries()].map(([id, games]) => ({
                    franchiseId: id,
                    franchiseName: data.franchises.find((f) => f.id === id)?.name ?? null,
                    games,
                }));
                return Promise.resolve(paginate(groups, request.cursor, request.limit));
            },
            listGamesPaginated(request) {
                return Promise.resolve(paginate(data.games, request.cursor, request.limit));
            },
            getGameById(gameId) {
                return Promise.resolve(data.games.find((g) => g.id === gameId) ?? null);
            },
            getGameTrophySets(gameId) {
                return Promise.resolve(data.trophySets
                    .filter((s) => s.gameId === gameId)
                    .sort((a, b) => a.platform.localeCompare(b.platform)));
            },
            getFranchise(franchiseId) {
                return Promise.resolve(data.franchises.find((f) => f.id === franchiseId) ?? null);
            },
            listRecentlyViewed(_userId, request) {
                return Promise.resolve(paginate(data.games, request.cursor, request.limit));
            },
        },
        trophies: {
            getTrophySet(trophySetId) {
                return Promise.resolve(data.trophySets.find((s) => s.id === trophySetId) ?? null);
            },
            listTrophyGroups(trophySetId) {
                return Promise.resolve(data.trophyGroups
                    .filter((g) => g.trophySetId === trophySetId)
                    .sort((a, b) => a.order - b.order));
            },
            listTrophies(trophySetId) {
                return Promise.resolve(data.trophies.filter((t) => t.trophySetId === trophySetId).sort((a, b) => a.order - b.order));
            },
            getTrophy(trophyId) {
                return Promise.resolve(data.trophies.find((t) => t.id === trophyId) ?? null);
            },
            listUserTrophies(_userId, _trophySetId) {
                // Ohne verbundenes PSN-Konto gibt es keinen Earned State – und es wird
                // bewusst keiner erfunden.
                return Promise.resolve([]);
            },
            getCustomDisplayAssets(scopeIds) {
                const set = new Set(scopeIds);
                return Promise.resolve(data.customDisplayAssets.filter((a) => set.has(a.targetId)));
            },
        },
        roadmaps: {
            getRoadmap(trophySetId) {
                return Promise.resolve(data.roadmaps.find((r) => r.trophySetId === trophySetId) ?? null);
            },
            listPhases(roadmapId) {
                return Promise.resolve(data.roadmapPhases.filter((p) => p.roadmapId === roadmapId).sort((a, b) => a.order - b.order));
            },
            listSteps(roadmapId) {
                const phaseIds = new Set(data.roadmapPhases.filter((p) => p.roadmapId === roadmapId).map((p) => p.id));
                return Promise.resolve(data.roadmapSteps.filter((s) => phaseIds.has(s.phaseId)));
            },
            listStepTrophies(roadmapId) {
                const phaseIds = new Set(data.roadmapPhases.filter((p) => p.roadmapId === roadmapId).map((p) => p.id));
                const stepIds = new Set(data.roadmapSteps.filter((s) => phaseIds.has(s.phaseId)).map((s) => s.id));
                return Promise.resolve(data.roadmapStepTrophies.filter((l) => stepIds.has(l.roadmapStepId)));
            },
            listStepMissables(_roadmapId) {
                return Promise.resolve(data.roadmapStepMissables);
            },
            listStepMapLinks(_roadmapId) {
                return Promise.resolve(data.roadmapStepMapLinks);
            },
            listUserStepStates(userGameRunId) {
                return Promise.resolve([...stepStates.values()].filter((s) => s.userGameRunId === userGameRunId));
            },
            setStepCompleted(input) {
                stepStates.set(`${input.userGameRunId}:${input.roadmapStepId}`, {
                    userGameRunId: input.userGameRunId,
                    roadmapStepId: input.roadmapStepId,
                    completed: input.completed,
                });
                return Promise.resolve();
            },
        },
        completion: {
            getSchema(trophySetId) {
                return Promise.resolve(data.completionSchemas.find((s) => s.trophySetId === trophySetId) ?? null);
            },
            listSections(schemaId) {
                return Promise.resolve(data.completionSections
                    .filter((s) => s.schemaId === schemaId)
                    .sort((a, b) => a.order - b.order));
            },
            listCategories(schemaId) {
                return Promise.resolve(categoriesOfSchema(schemaId).sort((a, b) => a.order - b.order));
            },
            listItems(schemaId) {
                const categoryIds = new Set(categoriesOfSchema(schemaId).map((c) => c.id));
                return Promise.resolve(data.completionItems
                    .filter((i) => categoryIds.has(i.categoryId))
                    .sort((a, b) => a.order - b.order));
            },
            listUserProgress(userGameRunId) {
                return Promise.resolve([...itemProgress.values()].filter((p) => p.userGameRunId === userGameRunId));
            },
            setItemProgress(input) {
                itemProgress.set(`${input.userGameRunId}:${input.itemId}`, {
                    userGameRunId: input.userGameRunId,
                    itemId: input.itemId,
                    current: input.current,
                });
                return Promise.resolve();
            },
        },
        users: {
            getProfile(_userId) {
                return Promise.resolve(null);
            },
            updateProfile() {
                return Promise.reject(new Error('Profil-Update braucht die echte Datenbank.'));
            },
            getPsnAccount(_userId) {
                return Promise.resolve(null);
            },
            listRuns(userId, request) {
                const list = [...runs.values()].filter((r) => r.userId === userId);
                return Promise.resolve(paginate(list, request.cursor, request.limit));
            },
            getRun(input) {
                return Promise.resolve(runs.get(`${input.userId}:${input.trophySetId}`) ?? null);
            },
            startRun(input) {
                const key = `${input.userId}:${input.trophySetId}`;
                const existing = runs.get(key);
                if (existing)
                    return Promise.resolve(existing);
                const run = {
                    id: key,
                    userId: input.userId,
                    trophySetId: input.trophySetId,
                    source: input.source,
                    startedAt: new Date().toISOString(),
                    lastActivityAt: null,
                    platinumEarnedAt: null,
                };
                runs.set(key, run);
                return Promise.resolve(run);
            },
            listNotes(input) {
                return Promise.resolve([...notes.values()].filter((n) => n.userId === input.userId && n.gameId === input.gameId));
            },
            upsertNote(input) {
                const id = input.noteId ?? `${input.userId}:${notes.size + 1}`;
                const now = new Date().toISOString();
                const note = {
                    id,
                    userId: input.userId,
                    gameId: input.gameId,
                    trophySetId: input.trophySetId,
                    body: input.body,
                    createdAt: notes.get(id)?.createdAt ?? now,
                    updatedAt: now,
                };
                notes.set(id, note);
                return Promise.resolve(note);
            },
            deleteNote(input) {
                notes.delete(input.noteId);
                return Promise.resolve();
            },
        },
        vmaps: {
            listLinksForGame(gameId) {
                return Promise.resolve(data.vmapsLinks.filter((l) => l.gameId === gameId));
            },
            listLinksForSource(input) {
                return Promise.resolve(data.vmapsLinks.filter((l) => l.sourceType === input.sourceType && l.sourceId === input.sourceId));
            },
        },
    };
}
//# sourceMappingURL=memory-api.js.map